import http from './http';

// Service du Dashboard Modération IA (contrat CD4, ticket B0_02 — étendu CD4+, ticket B5_01).
//
// http.js préfixe déjà /api et ajoute le header Authorization (gc_token). Signature réelle de
// http.get = un seul argument `path` (pas d'objet { params }) → query string construite à la main.
// Calque funnelService.js.
//
// ⚠ PROPRIÉTAIRE : B0_02 en Phase 1 (les 3 écrans du jalon 2 le consommaient sans le modifier),
// B5_01 en Phase 2 (CD4+) : B5_02 et B6_02 le CONSOMMENT sans le modifier. Sans cette règle,
// plusieurs fenêtres y écriraient une méthode chacune → collision de write-set.
//
// Les chemins et les formes de réponse sont gelés dans COHERENCE.md § Contrats gelés
// (CD1, CD2, CD3 — Phase 1 ; CD6 à CD11, CD14 — Phase 2).

const BASE = '/suadmin/moderation-dashboard';

// ─────────────────────────────────────────────────────────────────────────────
// ⛔ GARDE CD4+ — « HTTP 200 + { gcCode: 410 } » EST UN ÉCHEC.
//
// routes.js:191 pose `suadmin.use(auth.ensureAuthenticated)` AVANT `ensureAdmin`, et ce
// middleware répond `res.status(200).send({ gcCode: 410, … })` sur un JWT absent ou expiré
// (auth.js:903-906). Or http.js:9 ne lève que sur `!res.ok` : un `POST …/publish` sur session
// expirée SE RÉSOUT, et un `.then()` naïf annoncerait « publiée » alors que rien ne l'a été.
// Sans conséquence en lecture (l'écran affiche « aucune donnée »), mortel en écriture.
//
// Appliquée aux 7 méthodes de la Phase 2 ET à getAgents() (consommée par Agents.jsx, dans le
// write-set de B5_01). ⚠ PAS à getVerdicts()/getSites() : Monitoring.jsx:413 et
// SitesConfig.jsx:146 testent déjà `result.gcCode` dans leur `.then` et rédigent leur propre
// message — changer la méthode changerait le comportement de deux écrans hors write-set.
//
// ⚠ `err !== 'undefined'` : l'API renvoie parfois la CHAÎNE littérale 'undefined' dans ce champ
// (précédent withAuth.jsx:34, SitesConfig.jsx:145).
const SESSION_EXPIRED = 'session_expired';

function rejectGcCode(result) {
  // Un corps NON JSON (page d'erreur d'un proxy, réponse vide) n'est pas une session expirée :
  // http.js rend alors du texte — on le dit tel quel plutôt que d'envoyer se reconnecter.
  if (result === undefined || result === null || typeof result !== 'object') {
    const error = new Error('réponse inattendue du serveur (corps non JSON) : rien n\'a pu être interprété.');
    error.code = 'unexpected_response';
    error.status = 200;
    error.body = null;
    throw error;
  }
  const hasErr = result.err && result.err !== 'undefined';
  if (result.gcCode || hasErr) {
    const code = result && (result.gcCode || result.err);
    const error = new Error(
      `session expirée ou accès refusé (code ${code === undefined ? 'inconnu' : code}). Recharge la page pour te reconnecter.`
    );
    error.code = SESSION_EXPIRED;
    error.status = 200;
    error.body = result;
    throw error;
  }
  return result;
}

// Lecture typée d'une erreur levée par http.js (`throw new Error(\`HTTP ${status}: ${text}\`)`,
// http.js:11 — le corps JSON du contrôleur est CONCATÉNÉ dans le message) ou par rejectGcCode.
// Rend toujours la même forme, pour que les écrans branchent leur traitement sur `error`
// (le code métier du contrôleur : write_locked, prompt_drifted, draft_conflict, …) et jamais sur
// un fragment de message.
//   { status: number|null, error: string|null, message: string, body: object|null }
function parseApiError(err) {
  const message = (err && err.message) || 'erreur inconnue';
  if (err && err.code === SESSION_EXPIRED) {
    return { status: 200, error: SESSION_EXPIRED, message, body: err.body || null };
  }
  const match = /^HTTP (\d{3}):\s*([\s\S]*)$/.exec(message);
  if (!match) return { status: null, error: null, message, body: null };
  const status = Number(match[1]);
  const text = match[2];
  const brace = text.indexOf('{');
  if (brace === -1) return { status, error: null, message: text || message, body: null };
  try {
    const body = JSON.parse(text.slice(brace));
    if (!body || typeof body !== 'object') return { status, error: null, message, body: null };
    return {
      status,
      error: typeof body.error === 'string' ? body.error : null,
      message: typeof body.message === 'string' ? body.message : message,
      body
    };
  } catch (parseError) {
    // Corps non-JSON (page d'erreur d'un proxy, corps tronqué) : on retombe sur le message brut.
    return { status, error: null, message, body: null };
  }
}

// ⚠ Les 6 ids réels contiennent des `:` (forme `ag:ecd2ff4e:20250204:…`) → encodeURIComponent
//   obligatoire dans le chemin. Express décode `req.params.agent_id` lui-même.
function promptPath(agentId, suffix) {
  return `${BASE}/prompts/${encodeURIComponent(agentId)}${suffix || ''}`;
}

// CD1 — verdicts agrégés sur une fenêtre, une ligne par site × régime (appliqué / suggestion).
// ⚠ Le paramètre de query est `website_id` (snake_case), l'argument JS est `websiteId`.
// `websiteId` est optionnel : omis → tous les sites.
//
// ⚠ APPELANTS (B2_01, B3_01) : une borne vide est OMISE de la query, pas envoyée vide — le
// serveur applique alors SA fenêtre par défaut. Un écran qui afficherait « du X au Y » avec un
// from/to momentanément vide montrerait donc une période différente de celle réellement
// calculée, sans aucune erreur. Même comportement que funnelService.js ; Funnel.js s'en
// protège en calculant toujours from/to dans son constructeur — faire pareil.
const getVerdicts = (from, to, websiteId) => {
  const params = [];
  if (from) params.push(`from=${encodeURIComponent(from)}`);
  if (to) params.push(`to=${encodeURIComponent(to)}`);
  if (websiteId) params.push(`website_id=${encodeURIComponent(websiteId)}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  return http.get(`${BASE}/verdicts${qs}`);
};

// CD2 — sites où l'IA tourne réellement (+ dormant_count). Aucune fenêtre temporelle :
// l'endpoint n'expose ni `from` ni `to`, c'est un état courant.
const getSites = () => http.get(`${BASE}/sites`);

// CD3 — les 6 agents de verdict Mistral (+ context_agent, l'agent de résumé, à part).
// ⚠ Ne porte JAMAIS la consigne d'essai (DESCRIPTORS figé à 6 + contexte, mistralAgents.js:295).
const getAgents = () => http.get(`${BASE}/agents`).then(rejectGcCode);

// ── Phase 2 (B5_01, contrat CD4+) ─────────────────────────────────────────────

// CD6 — registre + réconciliation par agent. `200` même en dégradation totale de Mistral
// (`live: null`, `reconciliation: 'unknown'`, `live_error` renseigné) : le registre est une
// donnée LOCALE, il reste lisible. 7 entrées hors production (consigne d'essai `is_test_prompt`),
// 6 en production. ⛔ `prompts[].instructions` est ABSENT — le texte en service se lit par CD3.
const getPrompts = () => http.get(`${BASE}/prompts`).then(rejectGcCode);

// CD11 — historique complet d'un agent, texte de CHAQUE version (jamais tronqué), tri
// `internal_version` décroissant. 400 agent_not_whitelisted sur un id inconnu.
const getPromptVersions = (agentId) => http.get(promptPath(agentId, '/versions')).then(rejectGcCode);

// CD8 — brouillon, un seul par agent, mis à jour en place (D21). Aucun appel réseau côté API.
// Corps : { instructions, note?, expected_updated_at? } — 409 draft_conflict + current{author,
// updated_at} si le brouillon d'un AUTRE auteur serait écrasé sans expected_updated_at (D46).
const savePromptDraft = (agentId, payload) =>
  http.post(promptPath(agentId, '/draft'), payload).then(rejectGcCode);

// CD9 — publication : PATCH chez Mistral → relecture → registre. Corps :
// { instructions?, note, confirm: true, expected_updated_at? } — ou { record_only: true }
// (rattrapage C11, sans PATCH). ⛔ `confirm: true` (D43) et `note` (D45) sont OBLIGATOIRES.
const publishPrompt = (agentId, payload) =>
  http.post(promptPath(agentId, '/publish'), payload).then(rejectGcCode);

// CD10 — retour arrière : PATCH …/version chez Mistral, append-only (revenir à la v3 produit
// une version NEUVE). Corps : { internal_version, note, confirm: true } — la cible est NOTRE
// numéro (`internal_version`), jamais le numéro Mistral (réécrit en place par C10).
const rollbackPrompt = (agentId, payload) =>
  http.post(promptPath(agentId, '/rollback'), payload).then(rejectGcCode);

// CD7 — adoption unitaire (remise à niveau / résolution de dérive). 409 agent_not_adoptable
// quand l'agent est valide mais illisible ou vide chez Mistral.
const adoptPrompt = (agentId) => http.post(promptPath(agentId, '/adopt')).then(rejectGcCode);

// CD7 / D20 — amorçage global, chemin NOMINAL. UN instantané Mistral, un RAPPORT par agent :
// { adopted[], updated[], unchanged[], skipped[{agent_id, reason, live_status}], fetched_at }.
// ⛔ Toujours 200, même quand une adoption échoue : l'échec est dans `skipped[]`. Ne jamais
//    conclure « amorçage réussi » sans lire `skipped`.
const adoptAllPrompts = () => http.post(`${BASE}/prompts/adopt-all`).then(rejectGcCode);

const moderationDashboardService = {
  getVerdicts,
  getSites,
  getAgents,
  getPrompts,
  getPromptVersions,
  savePromptDraft,
  publishPrompt,
  rollbackPrompt,
  adoptPrompt,
  adoptAllPrompts,
  parseApiError,
  SESSION_EXPIRED
};
export default moderationDashboardService;
export {
  getVerdicts,
  getSites,
  getAgents,
  getPrompts,
  getPromptVersions,
  savePromptDraft,
  publishPrompt,
  rollbackPrompt,
  adoptPrompt,
  adoptAllPrompts,
  parseApiError,
  SESSION_EXPIRED
};
